import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { AlertTriangle, ListTodo, CalendarRange } from "lucide-react";

export default function Reports() {
  const [overdue, setOverdue] = useState(null);
  const [aged, setAged] = useState(null);
  const [byMonth, setByMonth] = useState(null);

  useEffect(() => {
    api.get("/reports/overdue-by-client").then((r) => setOverdue(r.data)).catch(() => {});
    api.get("/reports/aged-tasks").then((r) => setAged(r.data)).catch(() => {});
    api.get("/reports/deadlines-by-month", { params: { months: 6 } }).then((r) => setByMonth(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader testid="page-reports" title="Reports" subtitle="Practice health at a glance · Europe/London" />
      <div className="p-6 lg:p-8">
        <Tabs defaultValue="overdue">
          <TabsList data-testid="reports-tabs">
            <TabsTrigger value="overdue" data-testid="tab-overdue"><AlertTriangle className="h-3.5 w-3.5 mr-1" /> Overdue by client</TabsTrigger>
            <TabsTrigger value="aged" data-testid="tab-aged"><ListTodo className="h-3.5 w-3.5 mr-1" /> Aged tasks</TabsTrigger>
            <TabsTrigger value="deadlines" data-testid="tab-deadlines"><CalendarRange className="h-3.5 w-3.5 mr-1" /> Deadlines by month</TabsTrigger>
          </TabsList>

          {/* Overdue by client */}
          <TabsContent value="overdue" className="mt-4 space-y-3">
            <div className="text-sm text-slate-600">
              {overdue ? `${overdue.total_overdue_jobs} overdue job(s) across ${overdue.clients.length} client(s)` : "Loading…"}
            </div>
            <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2">Client</th>
                    <th className="text-left px-4 py-2">Overdue jobs</th>
                    <th className="text-left px-4 py-2">Worst</th>
                    <th className="text-left px-4 py-2">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {overdue?.clients?.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-500">Nothing overdue — well done.</td></tr>
                  )}
                  {overdue?.clients?.map((c) => (
                    <tr key={c.client_id} data-testid="overdue-row" className="border-t border-slate-100 hover:bg-slate-50 transition-colors align-top">
                      <td className="px-4 py-3">
                        <Link to={`/clients/${c.client_id}`} className="font-medium text-slate-900 hover:text-blue-600 transition-colors">{c.client_name}</Link>
                      </td>
                      <td className="px-4 py-3 font-mono">{c.overdue_count}</td>
                      <td className="px-4 py-3 font-mono text-rose-600">{c.worst_days_overdue}d</td>
                      <td className="px-4 py-3">
                        <ul className="space-y-1">
                          {c.jobs.map((j) => (
                            <li key={j.job_id} className="text-xs flex items-center justify-between">
                              <Link to={`/jobs/${j.job_id}`} className="text-slate-700 hover:text-blue-600 transition-colors">{j.title}</Link>
                              <span className="font-mono text-rose-600 ml-3">{fmtDate(j.deadline)} · {j.days_overdue}d</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Aged tasks */}
          <TabsContent value="aged" className="mt-4 space-y-3">
            <div className="text-sm text-slate-600">{aged ? `${aged.total_open} open task(s)` : "Loading…"}</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {aged && ["0-7d", "8-30d", "31-60d", "61-90d", "90d+"].map((k) => (
                <div key={k} className="border border-slate-200 rounded-md bg-white p-4">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{k}</div>
                  <div className={`mt-2 font-mono text-2xl ${k === "90d+" ? "text-rose-600" : k === "61-90d" ? "text-amber-600" : "text-slate-900"}`}>{aged.totals[k]}</div>
                </div>
              ))}
            </div>
            <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.1em] text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2">Task</th>
                    <th className="text-left px-4 py-2">Client · Job</th>
                    <th className="text-left px-4 py-2">Age</th>
                    <th className="text-left px-4 py-2">Bucket</th>
                  </tr>
                </thead>
                <tbody>
                  {aged && ["90d+", "61-90d", "31-60d", "8-30d", "0-7d"].flatMap((k) =>
                    (aged.buckets[k] || []).map((t) => (
                      <tr key={t.task_id} data-testid="aged-task-row" className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-slate-800">{t.title}</td>
                        <td className="px-4 py-3 text-slate-600">
                          <Link to={`/clients/${t.client_id}`} className="hover:text-blue-600 transition-colors">{t.client_name}</Link>
                          {t.job_title && <span className="text-slate-400"> · </span>}
                          {t.job_title && <Link to={`/jobs/${t.job_id}`} className="hover:text-blue-600 transition-colors">{t.job_title}</Link>}
                        </td>
                        <td className="px-4 py-3 font-mono">{t.age_days}d</td>
                        <td className="px-4 py-3"><StatusBadge status={k === "90d+" ? "overdue" : k === "61-90d" ? "on_hold" : "not_started"} label={k} /></td>
                      </tr>
                    ))
                  )}
                  {aged?.total_open === 0 && (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-500">No open tasks — inbox zero.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Deadlines by month */}
          <TabsContent value="deadlines" className="mt-4 space-y-3">
            <div className="text-sm text-slate-600">{byMonth ? `${byMonth.total_jobs} deadline(s) across the next ${byMonth.months.length} month(s)` : "Loading…"}</div>
            {byMonth?.months?.length === 0 && (
              <div className="border border-slate-200 rounded-md bg-white p-10 text-center text-slate-500">Nothing scheduled in the next 6 months.</div>
            )}
            {byMonth?.months?.map((m) => (
              <section key={m.month} className="border border-slate-200 rounded-md bg-white">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                  <div className="font-heading text-base font-semibold text-slate-900">{m.label}</div>
                  <span className="font-mono text-xs text-slate-500">{m.count} deadline(s)</span>
                </div>
                <ul className="divide-y divide-slate-100">
                  {m.jobs.map((j) => (
                    <li key={j.job_id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                      <div className="min-w-0">
                        <Link to={`/jobs/${j.job_id}`} className="text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors">{j.title}</Link>
                        {j.deadline_calculated && <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800">calculated</span>}
                        <div className="text-xs text-slate-500">
                          <Link to={`/clients/${j.client_id}`} className="hover:text-blue-600 transition-colors">{j.client_name}</Link>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <div className="font-mono text-xs text-slate-800">{fmtDate(j.deadline)}</div>
                        <StatusBadge status={j.status} />
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
